import { useState, useEffect } from 'react';
import API from '../../api/axiosConfig';

const AXES = [
  { key: 'knowledge_score', label: 'المعرفة',        desc: 'مستوى الإلمام بالمعلومات والمفاهيم المطلوبة للوظيفة', icon: '📚', color: '#2563eb' },
  { key: 'skills_score',    label: 'المهارات',        desc: 'القدرة على تطبيق المعرفة في بيئة العمل',             icon: '🛠️', color: '#0f766e' },
  { key: 'attitude_score',  label: 'السلوك والموقف',  desc: 'الالتزام والتعاون وروح العمل الجماعي',               icon: '🤝', color: '#7c3aed' },
];

const GRADE_COLOR = {
  'A+': '#15803d', A: '#16a34a', B: '#1d4ed8',
   C:  '#a16207',  D: '#c2410c', F: '#b91c1c',
};

const calcGrade = (total) => {
  if (total === null || total === undefined) return null;
  if (total >= 90) return 'A+';
  if (total >= 80) return 'A';
  if (total >= 70) return 'B';
  if (total >= 60) return 'C';
  if (total >= 50) return 'D';
  return 'F';
};

const inputStyle = {
  width: '100%',
  border: '1px solid var(--border-color, #d1d5db)',
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: 14,
  color: 'var(--text-color)',
  backgroundColor: 'var(--background-color)',
  outline: 'none',
};

const KsaForm = ({ period, onSuccess, initialData = null }) => {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id:     initialData?.employee_id     ?? '',
    assessor_id:     initialData?.assessor_id     ?? '',
    knowledge_score: initialData?.knowledge_score ?? '',
    skills_score:    initialData?.skills_score    ?? '',
    attitude_score:  initialData?.attitude_score  ?? '',
    period:          period,
    assessed_at:     initialData?.assessed_at     ?? new Date().toISOString().split('T')[0],
    notes:           initialData?.notes           ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    API.get('/employees').then(res => {
      setEmployees(res.data?.data ?? res.data ?? []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setForm(f => ({ ...f, period }));
  }, [period]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const scores = [form.knowledge_score, form.skills_score, form.attitude_score]
    .map(Number).filter(s => !isNaN(s) && s !== 0);
  const liveTotal = scores.length
    ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 100) / 100
    : null;
  const liveGrade = calcGrade(liveTotal);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (initialData) {
        await API.put(`/ksa-assessments/${initialData.id}`, form);
      } else {
        await API.post('/ksa-assessments', form);
      }
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.message ?? 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--background-color)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} dir="rtl">
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-color)', marginBottom: 24 }}>
        {initialData ? 'تعديل تقييم KSA' : 'تقييم KSA جديد'}
      </h3>

      {/* Error */}
      {error && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Live Score Banner */}
      {liveTotal !== null && (
        <div style={{ backgroundColor: 'var(--border-color, #f9fafb)', borderRadius: 12, padding: 16, marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>الدرجة الإجمالية الحالية</p>
            <p style={{ fontSize: 32, fontWeight: 700, color: 'var(--text-color)', margin: '4px 0 0' }}>{liveTotal}%</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 40, fontWeight: 700, color: GRADE_COLOR[liveGrade] ?? '#374151', margin: 0 }}>{liveGrade}</p>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>التقدير</p>
          </div>
        </div>
      )}

      {/* Selects Row */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6">
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)', display: 'block', marginBottom: 6 }}>
            الموظف <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select value={form.employee_id} onChange={e => set('employee_id', e.target.value)} style={inputStyle}>
            <option value="">اختر الموظف</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        </div>
        <div className="col-12 col-sm-6">
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)', display: 'block', marginBottom: 6 }}>المقيّم (اختياري)</label>
          <select value={form.assessor_id} onChange={e => set('assessor_id', e.target.value)} style={inputStyle}>
            <option value="">اختر المقيّم</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        </div>
        <div className="col-12 col-sm-6">
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)', display: 'block', marginBottom: 6 }}>
            تاريخ التقييم <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input type="date" value={form.assessed_at} onChange={e => set('assessed_at', e.target.value)} style={inputStyle} />
        </div>
      </div>

      {/* Axes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 24 }}>
        {AXES.map(axis => (
          <div key={axis.key}>
            {/* Label Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 20 }}>{axis.icon}</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-color)', margin: 0 }}>{axis.label}</p>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>{axis.desc}</p>
              </div>
              {form[axis.key] !== '' && (
                <span style={{ fontSize: 14, fontWeight: 700, color: axis.color }}>{form[axis.key]}%</span>
              )}
            </div>

            {/* Slider */}
            <input
              type="range" min="0" max="100" step="5"
              value={form[axis.key] === '' ? 0 : form[axis.key]}
              onChange={e => set(axis.key, Number(e.target.value))}
              style={{ width: '100%', accentColor: axis.color, cursor: 'pointer' }}
            />

            {/* Scale Labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              <span>0 — ضعيف</span>
              <span>50 — مقبول</span>
              <span>100 — ممتاز</span>
            </div>

            {/* Quick Buttons */}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {[0, 25, 50, 60, 70, 80, 90, 100].map(v => (
                <button
                  key={v}
                  onClick={() => set(axis.key, v)}
                  style={{
                    fontSize: 12,
                    padding: '2px 10px',
                    borderRadius: 8,
                    border: `1px solid ${Number(form[axis.key]) === v ? axis.color : '#d1d5db'}`,
                    backgroundColor: Number(form[axis.key]) === v ? axis.color : 'transparent',
                    color: Number(form[axis.key]) === v ? '#fff' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Notes */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)', display: 'block', marginBottom: 6 }}>ملاحظات (اختياري)</label>
        <textarea
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="تعليقات أو توصيات التطوير..."
          rows={3}
          style={{ ...inputStyle, resize: 'none' }}
        />
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={loading || !form.employee_id}
        style={{
          width: '100%',
          backgroundColor: loading || !form.employee_id ? '#a78bfa' : '#7c3aed',
          color: '#fff',
          border: 'none',
          borderRadius: 12,
          padding: '12px 0',
          fontSize: 15,
          fontWeight: 600,
          cursor: loading || !form.employee_id ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.2s',
        }}
      >
        {loading ? 'جاري الحفظ...' : initialData ? 'حفظ التعديلات' : 'حفظ التقييم'}
      </button>
    </div>
  );
};

export default KsaForm;